import { Grid } from '@chakra-ui/react';
import { LooseObject, PartialStateCode } from 'api/types';
import i18n from 'i18next';
import { get, isNull } from 'lodash';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { statusCodes } from '../../../foundations/theme';
import { getEmptyStateChart } from '../../../helpers/chartHelper';

const renderTranslatedTooltipText = (value: number, name: string, statusKey: string, statusLabels: Array<string>) => {
  let returnValue: boolean | (number | string)[] = false;
  statusLabels.forEach((statusLabel) => {
    if (name.includes('none')) {
      returnValue = [value, String(i18n.t('common:none') ?? '')];
    }
    if (i18n.exists(`${statusKey}:status.${statusLabel}.${name}.label`)) {
      returnValue = [value, String(i18n.t(`${statusKey}:status.${statusLabel}.${name}.label`) ?? '')];
    }
  });
  return returnValue;
};

const getStatusReasonChartData = (
  data: Array<{ status: number; status_reason: number; total: number }>,
  currentStatusCodes: PartialStateCode<string>,
  reasons: PartialStateCode<string>,
  statusKey: string,
) => {
  const chartData: object[] = [];

  Object.keys(currentStatusCodes).forEach((currentStatusKey: string) => {
    const reasonsForCurrentStatus = get(reasons, currentStatusKey);
    const currentStatusString = currentStatusCodes[currentStatusKey];
    let objectToPush: LooseObject = {
      label: i18n.t(`${statusKey}:status.${currentStatusString}.label`),
      cellProps: {},
    };

    const noneDataPoint = data?.find((obj) => obj.status === parseInt(currentStatusKey) && isNull(obj.status_reason));
    if (noneDataPoint) {
      objectToPush['none_' + currentStatusKey] = noneDataPoint?.total;
      objectToPush.cellProps['none_' + currentStatusKey] = 'gray';
    }
    if (reasonsForCurrentStatus) {
      Object.keys(reasonsForCurrentStatus).forEach((reason: string) => {
        //@ts-ignore
        const currentReasonString: string = reasonsForCurrentStatus[reason];
        const dataPoint = data?.find((obj) => obj.status === parseInt(currentStatusKey) && obj.status_reason === parseInt(reason));

        objectToPush[currentReasonString] = dataPoint ? dataPoint?.total : 0;

        objectToPush.cellProps[currentReasonString] = (statusCodes as any)?.[currentStatusString]?.[currentReasonString] || 'gray';
      });
    }
    chartData.push(objectToPush);
  });
  return chartData;
};

const getBars = (statusCodes: PartialStateCode<number>, reasons: string[], chartData: any[]) => {
  // eslint-disable-next-line no-undef
  let bars: JSX.Element[] = [];

  Object.keys(statusCodes).forEach((status) => {
    // @ts-ignore
    const statusReasons = reasons[status];
    bars.push(<Bar key={'none_' + status} legendType={'none'} dataKey={'none_' + status} stackId={status} fill="#896498" />);
    if (statusReasons) {
      Object.keys(statusReasons).forEach((reason, index) => {
        const color = chartData.filter((obj) => obj.cellProps[statusReasons[reason]]);
        bars.push(
          <Bar
            key={index + statusReasons[reason]}
            dataKey={statusReasons[reason]}
            stackId={status}
            fill={color[0].cellProps[statusReasons[reason]]}
          />,
        );
      });
    }
  });
  return bars;
};

const StatusReasonBarChart = (props: {
  data: Array<{ status: number; status_reason: number; total: number }>;
  statusKey: string;
  statusCodes: PartialStateCode<number>;
  reasons: Array<string>;
}) => {
  const { data, statusCodes, reasons, statusKey } = props;
  const statusLabels = Object.values(statusCodes);

  let loadingDataInterval: { label: string }[] = [];
  Object.values(statusCodes).forEach((status) => {
    loadingDataInterval.push({ label: i18n.t(`${statusKey}:status.${status}.label`) });
  });

  const loadingData = getEmptyStateChart(loadingDataInterval, statusLabels);
  // @ts-ignore
  const chartData = getStatusReasonChartData(data, statusCodes, reasons, statusKey);

  return (
    <Grid>
      <ResponsiveContainer aspect={3.1}>
        <BarChart
          barSize={10}
          width={500}
          height={300}
          data={chartData.length === 0 ? loadingData : chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          barCategoryGap={2}
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" />
          <YAxis domain={[0, (dataMax: number) => dataMax + dataMax * 0.1]} />
          <Tooltip
            //@ts-ignore
            formatter={(value: number, name: string) => renderTranslatedTooltipText(value, name, statusKey, statusLabels)}
          />
          {getBars(statusCodes, reasons, chartData).map((bar) => bar)}
        </BarChart>
      </ResponsiveContainer>
    </Grid>
  );
};

export default StatusReasonBarChart;
